/** Who a product is for, as marketplaces' Department item specific names it. */
export enum SupplyIntelDepartment {
  Women = "Women",
  Men = "Men",
  Unisex = "Unisex",
  Girls = "Girls",
  Boys = "Boys",
}

/** A Shopify metafield, by namespace and key. */
interface SupplyIntelMetafield {
  namespace: string;
  key: string;
}

// cspell:ignore flexify verishop
/** Shopify metafields stores use for a product's gender, most specific first. */
const GENDER_METAFIELDS: readonly SupplyIntelMetafield[] = [
  { namespace: "custom", key: "department" },
  { namespace: "product", key: "department" },
  { namespace: "custom", key: "gender" },
  { namespace: "custom", key: "target_gender" },
  { namespace: "mm-google-shopping", key: "gender" },
  { namespace: "google", key: "gender" },
  { namespace: "custom", key: "google_gender" },
  { namespace: "mc-facebook", key: "gender" },
  { namespace: "msft_bingads", key: "gender" },
  { namespace: "simprosys", key: "gender" },
  { namespace: "global", key: "gender" },
  { namespace: "my_fields", key: "gender" },
  { namespace: "flexify", key: "gender" },
  { namespace: "verishop", key: "suitable_gender" },
];
/** Shopify metafields stores use for a product's age group. */
const AGE_GROUP_METAFIELDS: readonly SupplyIntelMetafield[] = [
  { namespace: "mm-google-shopping", key: "age_group" },
  { namespace: "google", key: "age_group" },
  { namespace: "custom", key: "age_group" },
];

/** The metafields a department is read from, on the variant or its product family. */
export const SUPPLY_INTEL_DEPARTMENT_METAFIELDS: readonly SupplyIntelMetafield[] =
  [...GENDER_METAFIELDS, ...AGE_GROUP_METAFIELDS];

export interface SupplyIntelMetafieldValue {
  metafield: SupplyIntelMetafield;
  /** Whether the value is set on the variant or on its product family. */
  level: "variant" | "family";
  /** The stored JSON value: a string, or a list of strings. */
  value: unknown;
}

type Gender = "female" | "male" | "unisex";

const GENDER_WORDS: Record<string, { gender: Gender; kids: boolean }> = {
  female: { gender: "female", kids: false },
  women: { gender: "female", kids: false },
  womens: { gender: "female", kids: false },
  woman: { gender: "female", kids: false },
  ladies: { gender: "female", kids: false },
  male: { gender: "male", kids: false },
  men: { gender: "male", kids: false },
  mens: { gender: "male", kids: false },
  man: { gender: "male", kids: false },
  unisex: { gender: "unisex", kids: false },
  girl: { gender: "female", kids: true },
  girls: { gender: "female", kids: true },
  boy: { gender: "male", kids: true },
  boys: { gender: "male", kids: true },
};
const KIDS_AGE_GROUPS = new Set([
  "kids",
  "kid",
  "toddler",
  "infant",
  "newborn",
]);
const ADULT_AGE_GROUPS = new Set(["adult", "adults"]);

/** A value's words, such as ["men", "s", "apparel"] for "Men's Apparel"; a list value's words together. */
function words(value: unknown): string[] {
  const values = Array.isArray(value) ? value : [value];
  return values.flatMap((entry) =>
    typeof entry === "string"
      ? entry
          .toLowerCase()
          .split(/[^a-z]+/)
          .filter(Boolean)
      : [],
  );
}

/** Ranks a value by where it came from: variant before family, then the metafield's order. */
function rank(
  value: SupplyIntelMetafieldValue,
  metafields: readonly SupplyIntelMetafield[],
): number | null {
  const index = metafields.findIndex(
    (metafield) =>
      metafield.namespace === value.metafield.namespace.toLowerCase() &&
      metafield.key === value.metafield.key.toLowerCase(),
  );
  if (index < 0) {
    return null;
  }
  return (value.level === "variant" ? 0 : metafields.length) + index;
}

/** What the highest-ranked value that says anything says. */
function firstMatch<T>(
  values: readonly SupplyIntelMetafieldValue[],
  metafields: readonly SupplyIntelMetafield[],
  read: (words: string[]) => T | undefined,
): T | undefined {
  return values
    .flatMap((value) => {
      const order = rank(value, metafields);
      return order === null ? [] : [{ order, value: value.value }];
    })
    .sort((a, b) => a.order - b.order)
    .map(({ value }) => read(words(value)))
    .find((match) => match !== undefined);
}

/** A value naming both men and women, such as "Men/Women", is unisex. */
function readGender(
  valueWords: string[],
): { gender: Gender; kids: boolean } | undefined {
  const matches = valueWords.flatMap((word) => GENDER_WORDS[word] ?? []);
  if (matches.length === 0) {
    return undefined;
  }
  const genders = new Set(matches.map((match) => match.gender));
  return {
    gender:
      genders.has("unisex") || genders.size > 1
        ? "unisex"
        : (matches[0]?.gender ?? "unisex"),
    kids: matches.some((match) => match.kids),
  };
}

function readKids(valueWords: string[]): boolean | undefined {
  if (valueWords.some((word) => KIDS_AGE_GROUPS.has(word))) {
    return true;
  }
  return valueWords.some((word) => ADULT_AGE_GROUPS.has(word))
    ? false
    : undefined;
}

/**
 * A unit's department from its product's gender and age group metafields;
 * null when they don't say, or say unisex for kids, which has no department.
 */
export function supplyIntelDepartment(
  values: readonly SupplyIntelMetafieldValue[],
): SupplyIntelDepartment | null {
  const gender = firstMatch(values, GENDER_METAFIELDS, readGender);
  if (!gender) {
    return null;
  }
  const kids =
    gender.kids || firstMatch(values, AGE_GROUP_METAFIELDS, readKids) === true;
  switch (gender.gender) {
    case "female":
      return kids ? SupplyIntelDepartment.Girls : SupplyIntelDepartment.Women;
    case "male":
      return kids ? SupplyIntelDepartment.Boys : SupplyIntelDepartment.Men;
    case "unisex":
      return kids ? null : SupplyIntelDepartment.Unisex;
  }
}
